import { useTranslation } from '@/lib/i18n';
import type { Money, PayoutTransaction } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { PayoutStatusPill } from './PayoutStatusPill';

export interface PayoutTableProps {
  items: ReadonlyArray<PayoutTransaction>;
}

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
};

export const PayoutTable = ({ items }: PayoutTableProps) => {
  const { t } = useTranslation('settlement');
  if (items.length === 0) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="rounded-md p-4 text-sm text-[color:var(--content-muted)]"
      >
        {t('settlement.payouts.empty')}
      </p>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="payout-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          <TableRow>
            <TableHead scope="col">{t('settlement.payouts.columns.payoutTxId')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.attempt')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.status')}</TableHead>
            <TableHead scope="col" className="text-right">{t('settlement.payouts.columns.amount')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.transactionNo')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.initiatedAt')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.completedAt')}</TableHead>
            <TableHead scope="col">{t('settlement.payouts.columns.errorMessage')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((tx) => (
            <TableRow
              key={tx.payoutTxId}
              data-testid="payout-row"
              data-status={tx.status}
              style={{ height: 'var(--density-compact-row-h)' }}
            >
              <TableCell className="font-mono text-xs">{tx.payoutTxId}</TableCell>
              <TableCell className="text-xs">#{tx.attemptNo}</TableCell>
              <TableCell>
                <PayoutStatusPill status={tx.status} payoutTxId={tx.payoutTxId} />
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatMoney(tx.amount)}
              </TableCell>
              <TableCell className="font-mono text-xs text-[color:var(--content-muted)]">
                {tx.transactionNo ?? '—'}
              </TableCell>
              <TableCell className="text-xs text-[color:var(--content-muted)]">
                {formatTime(tx.initiatedAt)}
              </TableCell>
              <TableCell className="text-xs text-[color:var(--content-muted)]">
                {formatTime(tx.completedAt)}
              </TableCell>
              <TableCell className="text-xs">
                {tx.errorCode ? (
                  <span style={{ color: 'var(--status-danger-fg)' }}>
                    {tx.errorCode}
                    {tx.errorMessage ? `: ${tx.errorMessage}` : ''}
                  </span>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
